import { Table as AntTable, type TablePaginationConfig, type TableProps as AntTableProps } from "antd";
import type { SorterResult, SortOrder } from "antd/es/table/interface";
import { forwardRef, useEffect, useMemo, useState } from "react";
import type { MouseEvent as ReactMouseEvent, ReactNode, TdHTMLAttributes, ThHTMLAttributes } from "react";
import "./GlobalTable.css";

function highlightText(text: string, search: string): ReactNode {
  if (!search) return text;
  const escaped = search.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
  const regex = new RegExp(`(${escaped})`, "gi");
  const parts = text.split(regex);
  if (parts.length === 1) return text;
  return parts.map((part, i) =>
    regex.test(part) ? (
      <mark key={i} style={{ background: "#ffe58f", padding: 0 }}>{part}</mark>
    ) : (
      part
    )
  );
}
import Button from "../button";
import { PaginationToolbar } from "../list/paginationToolbar";
import Checkbox, { CheckboxType } from "../inputs/checkbox";
import MaintenancePopup from "./MaintenancePopup";
import MaintenanceExitPopup from "./MaintenanceExitPopup";
import { useLazyGetMaintenanceStatusQuery, useSetMaintenanceStatusMutation } from "store/api";

const LegacyTableBodyCell = forwardRef<HTMLTableCellElement, TdHTMLAttributes<HTMLTableCellElement>>(
  function LegacyTableBodyCell({ children, ...rest }, ref) {
    return (
      <td {...rest} ref={ref}>
        <group>
          <text data-wrap="wrap" data-space-horizontal="5">{children}</text>
        </group>
      </td>
    );
  }
);
const LegacyTableHeaderCell = forwardRef<HTMLTableCellElement, ThHTMLAttributes<HTMLTableCellElement>>(
  function LegacyTableHeaderCell({ children, ...rest }, ref) {
    return (
      <th {...rest} ref={ref}>
        <group data-space-horizontal="5">
          {children}
        </group>
      </th>
    );
  }
);
type TableMode = "client" | "server";

type TableQuery = {
  page: number;
  pageSize: number;
  sortField?: string;
  sortOrder?: Extract<SortOrder, "ascend" | "descend">;
  filters?: Record<string, unknown>;
  search?: string;
};

type RowAction<T> = {
  key: string;
  label: string;
  onClick: (row: T) => void;
  icon?: string;
  ariaLabel?: string;
  danger?: boolean;
  hidden?: (row: T) => boolean;
  disabled?: (row: T) => boolean;
};

export interface GlobalTableProps<T extends Record<string, any>>
  extends Omit<AntTableProps<T>, "dataSource" | "columns" | "pagination" | "onChange"> {
  rowKey: string | ((row: T) => React.Key);
  columns: NonNullable<AntTableProps<T>["columns"]>;
  data: T[];
  mode?: TableMode;
  query?: TableQuery;
  onQueryChange?: (
    next: TableQuery,
    reason: "paginate" | "sort" | "filter" | "search"
  ) => void;
  total?: number;
  loading?: boolean;
  emptyText?: string;
  searchable?: boolean;
  searchPlaceholder?: string;
  rowActions?: RowAction<T>[];
  onNew?: () => void;
  onUpload?: () => void;
  isUploading?: boolean;
  onDownload?: () => void;
  onEditWidget?: () => void;
  pagination?: false | TablePaginationConfig;
  variant?: "default" | "legacy";
  tableTitle?: string;
}

type TableRowClassName<T extends Record<string, any>> = NonNullable<AntTableProps<T>["rowClassName"]>;

const defaultQuery: TableQuery = {
  page: 1,
  pageSize: 100,
};

function safeCompare(a: unknown, b: unknown) {
  if (a == null && b == null) return 0;
  if (a == null) return -1;
  if (b == null) return 1;
  if (typeof a === "number" && typeof b === "number") return a - b;
  return String(a).localeCompare(String(b));
}

/** Hide technical id column from UI (rowKey can still use id from data). */
function isHiddenIdColumn(col: { key?: React.Key; dataIndex?: string | string[] }) {
  const key = col.key != null ? String(col.key) : "";
  if (key.toLowerCase() === "id") return true;
  const di = col.dataIndex;
  if (typeof di === "string" && di.toLowerCase() === "id") return true;
  if (Array.isArray(di) && di.length && String(di[0]).toLowerCase() === "id") return true;
  return false;
}

function buildRowClassName<T extends Record<string, any>>(
  userRowClassName: AntTableProps<T>["rowClassName"] | undefined
): TableRowClassName<T> {
  return (record, index) => {
    const zebraClass = (index ?? 0) % 2 === 0 ? "global-table-row-even" : "global-table-row-odd";
    const userClassName = userRowClassName?.(record, index) ?? "";
    return [zebraClass, userClassName].filter(Boolean).join(" ");
  };
}

function isCopyAllowedTarget(target: EventTarget | null) {
  if (!(target instanceof HTMLElement)) return true;
  return !Boolean(target.closest("button, a, input, textarea, select, [role='button'], [data-no-cell-copy='true']"));
}

function fallbackCopyText(text: string) {
  const textarea = document.createElement("textarea");
  textarea.value = text;
  textarea.setAttribute("readonly", "true");
  textarea.style.position = "fixed";
  textarea.style.opacity = "0";
  document.body.appendChild(textarea);
  textarea.focus();
  textarea.select();
  try {
    return document.execCommand("copy");
  } catch {
    return false;
  } finally {
    document.body.removeChild(textarea);
  }
}

async function copyCellText(event: ReactMouseEvent<HTMLTableCellElement>) {
  if (!isCopyAllowedTarget(event.target)) return;

  const selected = window.getSelection?.()?.toString().trim() ?? "";
  if (selected.length > 0) return;

  const text = event.currentTarget.innerText.trim();
  if (!text) return;

  if (fallbackCopyText(text)) return;

  try {
    if (navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(text);
    }
  } catch {
    // Ignore clipboard API errors when browser blocks this capability.
  }
}

function GlobalTable<T extends Record<string, any>>({
  rowKey,
  columns,
  data,
  mode = "client",
  query,
  onQueryChange,
  total,
  loading,
  emptyText = "No data",
  searchable = false,
  searchPlaceholder = "Search...",
  rowActions = [],
  onNew,
  onUpload,
  isUploading = false,
  onDownload,
  onEditWidget,
  pagination,
  variant = "default",
  tableTitle,
  components: userComponents,
  scroll: userScroll,
  rowClassName: userRowClassName,
  ...rest
}: GlobalTableProps<T>) {
  const [innerQuery, setInnerQuery] = useState<TableQuery>(defaultQuery);
  const [isMaintenanceOn, setIsMaintenanceOn] = useState(false);
  const [isMaintenanceLoading, setIsMaintenanceLoading] = useState(false);
  const [isMaintenancePopupOpen, setIsMaintenancePopupOpen] = useState(false);
  const [isMaintenanceExitPopupOpen, setIsMaintenanceExitPopupOpen] = useState(false);
  const [fetchMaintenanceStatus] = useLazyGetMaintenanceStatusQuery();
  const [setMaintenanceStatus] = useSetMaintenanceStatusMutation();
  const effectiveQuery = query || innerQuery;
  const isEnvironmentTable = useMemo(
    () => /(^|[^a-z])(environnement|environment)([^a-z]|$)/i.test(String(tableTitle ?? "")),
    [tableTitle]
  );

  useEffect(() => {
    if (onQueryChange) return;
    setInnerQuery((prev) => {
      if (prev.page === 1 && (prev.search ?? "") === "") return prev;
      return { ...prev, page: 1, search: "" };
    });
  }, [onQueryChange, tableTitle]);

  useEffect(() => {
    if (!isEnvironmentTable) return;

    let isMounted = true;

    const loadMaintenanceState = async () => {
      setIsMaintenanceLoading(true);
      try {
        const result = await fetchMaintenanceStatus().unwrap();
        if (!isMounted) return;
        setIsMaintenanceOn(Boolean(result?.isInMaintenance));
      } catch {
        // ignore maintenance status read failures
      } finally {
        if (isMounted) {
          setIsMaintenanceLoading(false);
        }
      }
    };

    void loadMaintenanceState();

    return () => {
      isMounted = false;
    };
  }, [fetchMaintenanceStatus, isEnvironmentTable]);

  const updateMaintenance = async (nextValue: boolean, dateStart = "", dateEnd = "") => {
    if (isMaintenanceLoading) return false;

    const previousValue = isMaintenanceOn;
    setIsMaintenanceOn(nextValue);

    setIsMaintenanceLoading(true);
    try {
      await setMaintenanceStatus({
        isMaint: nextValue,
        dateStart,
        dateEnd,
      }).unwrap();
      return true;
    } catch {
      setIsMaintenanceOn(previousValue);
      return false;
    } finally {
      setIsMaintenanceLoading(false);
    }
  };

  const setQuery = (
    next: TableQuery,
    reason: "paginate" | "sort" | "filter" | "search"
  ) => {
    if (onQueryChange) onQueryChange(next, reason);
    else setInnerQuery(next);
  };

  const searchTerm = effectiveQuery.search ?? "";

  const highlightedColumns = useMemo(() => {
    if (!searchTerm) return columns;
    return columns.map((col: any) => {
      const originalRender = col.render;
      return {
        ...col,
        render: (value: unknown, row: any, index: number) => {
          const rendered = originalRender
            ? originalRender(value, row, index)
            : String(value ?? "");
          if (typeof rendered === "string") {
            return highlightText(rendered, searchTerm);
          }
          return rendered;
        },
      };
    });
  }, [columns, searchTerm]);

  const columnsWithActions = useMemo(() => {
    const visibleColumns = highlightedColumns
      .filter((c: any) => !isHiddenIdColumn(c as { key?: React.Key; dataIndex?: string | string[] }))
      .map((col: any) => {
        const previousOnCell = col.onCell;
        return {
          ...col,
          onCell: (record: T, index: number) => {
            const previousCellProps = previousOnCell?.(record, index) ?? {};
            const previousOnClick = previousCellProps.onClick as
              | ((event: ReactMouseEvent<HTMLTableCellElement>) => void)
              | undefined;

            return {
              ...previousCellProps,
              title: previousCellProps.title ?? "Click to copy",
              className: [previousCellProps.className, "global-table-copyable-cell"].filter(Boolean).join(" "),
              onClick: (event: ReactMouseEvent<HTMLTableCellElement>) => {
                previousOnClick?.(event);
                void copyCellText(event);
              },
            };
          },
        };
      });
    if (!rowActions.length) return visibleColumns;
    const hasHorizontalScroll = Boolean((userScroll as { x?: unknown } | undefined)?.x);
    const actionColumn = {
      key: "__actions",
      title: "action",
      width: 180,
      fixed: hasHorizontalScroll ? ("right" as const) : undefined,
      className: "global-table-action-col",
      render: (_: unknown, row: T) => (
        <group data-wrap="no" data-gap="10">
          {rowActions
            .filter((action) => !action.hidden?.(row))
            .map((action) => (
              <Button
                key={action.key}
                mini
                onClick={() => action.onClick(row)}
                {...(action.disabled?.(row) ? { disabled: true } : null)}
                {...(action.key === "delete" ? { "data-color": "main" } : null)}
                {...(action.danger ? { "data-color": "error-color" } : null)}
                {...(action.ariaLabel ? { "aria-label": action.ariaLabel } : null)}
              >
                {action.icon ? <icon>{action.icon}</icon> : action.label}
              </Button>
            ))}
        </group>
      ),
    };
    return [...visibleColumns, actionColumn];
  }, [highlightedColumns, rowActions, userScroll]);

  const clientRows = useMemo(() => {
    if (mode !== "client") return data;
    let items = [...data];

    if (effectiveQuery.search) {
      const q = effectiveQuery.search.toLowerCase();
      items = items.filter((row) =>
        Object.values(row).some((v) => String(v ?? "").toLowerCase().includes(q))
      );
    }

    if (effectiveQuery.sortField && effectiveQuery.sortOrder) {
      const coef = effectiveQuery.sortOrder === "descend" ? -1 : 1;
      const field = effectiveQuery.sortField;
      items.sort((a, b) => coef * safeCompare(a[field], b[field]));
    }

    const start = (effectiveQuery.page - 1) * effectiveQuery.pageSize;
    return items.slice(start, start + effectiveQuery.pageSize);
  }, [data, effectiveQuery, mode]);

  const clientTotal = useMemo(() => {
    if (mode !== "client") return total || data.length;
    if (!effectiveQuery.search) return data.length;
    const q = effectiveQuery.search.toLowerCase();
    return data.filter((row) =>
      Object.values(row).some((v) => String(v ?? "").toLowerCase().includes(q))
    ).length;
  }, [data, effectiveQuery.search, mode, total]);

  const tableData = mode === "client" ? clientRows : data;

  const effectiveTotal =
    mode === "client" ? clientTotal : total ?? 0;

  useEffect(() => {
    if (mode !== "client") return;

    const currentPage = effectiveQuery.page || 1;
    const pageSize = effectiveQuery.pageSize || defaultQuery.pageSize;
    const maxPage = Math.max(1, Math.ceil(clientTotal / pageSize));

    if (currentPage > maxPage) {
      setQuery({ ...effectiveQuery, page: maxPage }, "paginate");
      return;
    }

    if (currentPage < 1) {
      setQuery({ ...effectiveQuery, page: 1 }, "paginate");
    }
  }, [clientTotal, effectiveQuery, mode]);

  const userPaginationConfig: TablePaginationConfig =
    typeof pagination === "object" && pagination !== null ? pagination : {};

  const mergedComponents = useMemo(() => {
    if (variant !== "legacy") return userComponents;
    return {
      ...userComponents,
      body: {
        ...userComponents?.body,
        cell: LegacyTableBodyCell,
      },

      header: {
        ...userComponents?.header,
        cell: LegacyTableHeaderCell,
      },
    };
  }, [variant, userComponents]);

  const showPaginationBar = pagination !== false;

  const effectiveScroll = useMemo<AntTableProps<T>["scroll"]>(() => {
    if (!userScroll) return undefined;
    return userScroll;
  }, [userScroll]);

  const effectiveRowClassName = useMemo(
    () => buildRowClassName<T>(userRowClassName),
    [userRowClassName]
  );

  return (
    <group
      // className={`global-table ${variant === "legacy" ? "global-table--legacy" : ""}`}
      data-direction="column"
      data-gap="10"
      data-space="10"
    >
      {searchable && (
        <group
          //data-length="autofit"
          data-gap="10"
          data-height=""
          data-sticky="top"
          // data-space="10"
          data-backdrop=""
        >
          {onNew && (
            <group data-gap="10" data-width="auto" data-align="center">
              <div //NOSONAR - intentional div-based icon control per project UX pattern
                className="button primary"
                role="button"
                tabIndex={0}
                aria-label="New"
                onClick={onNew}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") onNew();
                }}
              >
                <icon>add</icon>
              </div>
            </group>
          )}
          <group data-length="autofit">
            <div className="form_fields">
              <div className="field_cont">
                <icon data-space="5">search</icon>
                <input
                  type="text"
                  placeholder={searchPlaceholder}
                  value={effectiveQuery.search ?? ""}
                  onChange={(e) =>
                    setQuery(
                      { ...effectiveQuery, page: 1, search: (e.target as HTMLInputElement).value as string },
                      "search"
                    )
                  }
                />
                {(effectiveQuery.search ?? "").length > 0 && (
                  <div //NOSONAR
                    role="button"
                    className="button mini"
                    aria-label="Clear search"
                    onClick={() =>
                      setQuery(
                        { ...effectiveQuery, page: 1, search: "" },
                        "search"
                      )
                    }
                  >
                    <icon data-icon-weight="300">close</icon>
                  </div>
                )}
                <i></i>
              </div>
            </div>
          </group>
          {
            (onUpload || onDownload || onEditWidget) && (<group data-gap="10" data-width="auto" data-align="center">
              {onEditWidget && (
                <div //NOSONAR
                  className="button primary"
                  role="button"
                  tabIndex={0}
                  aria-label="Edit widget"
                  onClick={onEditWidget}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") onEditWidget();
                  }}
                >
                  <icon>edit</icon>
                </div>
              )}
              {onUpload && (
                <div //NOSONAR
                  className="button primary"
                  role="button"
                  tabIndex={0}
                  aria-label="Upload"
                  aria-disabled={isUploading}
                  onClick={() => {
                    if (isUploading) return;
                    onUpload();
                  }}
                >
                  <icon>upload</icon>
                </div>
              )}
              {onDownload && (
                <div //NOSONAR
                  className="button primary"
                  role="button"
                  tabIndex={0}
                  aria-label="Download"
                  onClick={onDownload}
                >
                  <icon>download</icon>
                </div>
              )}
            </group>
        )}
        </group>
      )}
      {isEnvironmentTable && (
        <group data-space-horizontal="10" data-space="10">
          <Checkbox
            name="maintenance_switch"
            simple
            label="Maintenance"
            checkboxType={CheckboxType.StringValue}
            value={isMaintenanceOn ? "1" : "0"}
            stringCheckedValue="1"
            disabled={isMaintenanceLoading}
            onCustomChange={(_name, checked) => {
              const nextValue = Boolean(checked);
              if (nextValue) {
                setIsMaintenanceOn(true);
                setIsMaintenancePopupOpen(true);
                return;
              }
              setIsMaintenanceExitPopupOpen(true);
            }}
          />
        </group>
      )}
      <MaintenancePopup
        isOpen={isMaintenancePopupOpen}
        isSaving={isMaintenanceLoading}
        onClose={() => {
          setIsMaintenancePopupOpen(false);
          setIsMaintenanceOn(false);
        }}
        onConfirm={({ dateStart, dateEnd }) => {
          void updateMaintenance(true, dateStart, dateEnd).then((ok) => {
            if (ok) {
              setIsMaintenancePopupOpen(false);
            }
          });
        }}
      />
      <MaintenanceExitPopup
        isOpen={isMaintenanceExitPopupOpen}
        isSaving={isMaintenanceLoading}
        onClose={() => {
          setIsMaintenanceExitPopupOpen(false);
          setIsMaintenanceOn(true);
        }}
        onConfirm={() => {
          void updateMaintenance(false).then((ok) => {
            if (ok) {
              setIsMaintenanceExitPopupOpen(false);
            }
          });
        }}
      />
      <group
        // className="global-table-panel"
        data-border=""
        //data-radius="10"
        data-contain=""
        data-direction="column"
        data-wrap="no"
        data-height="autofit"
        data-gap="10"
      //data-space="10"
      >
        <group data-align="center" data-space="10" data-gap="10">
          <text data-weight="600">{tableTitle}</text>
          {/* <separator data-height="20" data-vertical=""></separator>
          <text>14</text> */}
        </group>
        <group data-height="autofit" data-space="1">
          <view className="table_cont" data-background="none">
            <AntTable<T>
              rowKey={rowKey}
              rowClassName={effectiveRowClassName}
              columns={columnsWithActions}
              dataSource={tableData}
              components={mergedComponents}
              scroll={effectiveScroll}
              loading={loading}
              bordered={variant === "legacy"}
              size={variant === "legacy" ? "small" : "middle"}
              locale={{ emptyText }}
              pagination={false}
              onChange={(
                _tablePagination,
                filters,
                sorter: SorterResult<T> | SorterResult<T>[]
              ) => {
                const s = (Array.isArray(sorter) ? sorter[0] : sorter) as SorterResult<T>;
                const sortField =
                  (typeof s.field === "string" ? s.field : undefined) ||
                  ((s.column as any)?.sortField as string | undefined);
                const reason: "paginate" | "sort" | "filter" =
                  s.order ? "sort" : Object.keys(filters || {}).length ? "filter" : "paginate";
                const next: TableQuery = {
                  ...effectiveQuery,
                  page: reason === "sort" || reason === "filter" ? 1 : effectiveQuery.page,
                  pageSize: effectiveQuery.pageSize,
                  sortField,
                  sortOrder: (s.order as TableQuery["sortOrder"]) || undefined,
                  filters: filters as Record<string, unknown>,
                };
                setQuery(next, reason);
              }}
              showSorterTooltip={false}
              {...rest}
            />
          </view>
        </group>
        {showPaginationBar && (
          <PaginationToolbar
            className="global-table-pagination-wrap"
            data-space="10"
            total={effectiveTotal}
            pagination={{
              ...userPaginationConfig,
              current: effectiveQuery.page,
              pageSize: effectiveQuery.pageSize,
              hideOnSinglePage: false,
            }}
            setPagination={(next) => {
              const pageSizeChanged = next.pageSize !== effectiveQuery.pageSize;
              setQuery(
                {
                  ...effectiveQuery,
                  page: pageSizeChanged ? 1 : (next.current ?? effectiveQuery.page),
                  pageSize: next.pageSize ?? effectiveQuery.pageSize,
                },
                "paginate"
              );
            }}
          />
        )}
      </group>
    </group>
  );
}

export default GlobalTable;
